import IconEdit from 'assets/icons/edit.svg';
import Button from 'components/button';
import InlineIntervalSelector from 'components/inline-interval-selector';
import Badge from 'components/legacy-badge';
import ModalSlideUp from 'components/modal-slide-up';
import { SubscriptionInterval, getIntervalTranslationKeyShort } from 'constants/order';
import { useCart } from 'contexts/cart';
import useMobile from 'hooks/common/use-mobile';
import useSubscriptionDiscount from 'hooks/subscriptions/use-subscription-discount';
import { EffectiveCart } from 'interfaces/cart';
import { isSubscriptionItem } from 'interfaces/line-item';
import { useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Gtm } from 'tracking';
import { toCartItem } from 'contexts/cart/utils/to-cart-item';

interface SubscriptionButtonProps {
  item: EffectiveCart['items'][0];
  canEditSubscription: boolean;
}

const SubscriptionButton: React.FC<SubscriptionButtonProps> = ({ item, canEditSubscription }) => {
  const { updateCart } = useCart();
  const intervalRef = useRef(
    (isSubscriptionItem(item) && (item.interval as SubscriptionInterval)) || null
  );
  const [interval, setInterval] = useState<SubscriptionInterval | null>(intervalRef.current);

  const [isOpen, setIsOpen] = useState(false);

  const { singleProductDiscount: discountPercentage } = useSubscriptionDiscount({
    item,
  });

  const intl = useIntl();
  const isMobile = useMobile();

  const handleClose = useCallback(async (): Promise<void> => {
    if (interval === intervalRef.current) {
      setIsOpen(false);
      return;
    }

    intervalRef.current = interval;

    updateCart([
      {
        target: toCartItem({ item, shouldQuantity: false }),
        updateData: {
          variantId: String(item.variantId),
          quantity: item.quantity,
          interval: interval as SubscriptionInterval,
        },
      },
    ]);

    setIsOpen(false);
    Gtm.track({
      group: Gtm.Group.Cart,
      name: Gtm.CartEvent.SelectIntervalOption,
      ...(interval ? { data: { type: interval } } : {}),
    });
  }, [interval, item, updateCart]);
  return (
    <>
      <div className="flex items-center justify-end">
        {item.subscribable && canEditSubscription && (
          <button
            className="border-grey-200 text-grey-900 flex items-center rounded-lg border p-2 text-xs font-black uppercase"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(true);
              Gtm.track({
                group: Gtm.Group.Cart,
                name: Gtm.CartEvent.OpenSubscriptionOptions,
              });
            }}
          >
            <IconEdit className="mr-1 mb-1 h-3 w-3" />
            {isSubscriptionItem(item) ? (
              <>
                <FormattedMessage
                  id={
                    getIntervalTranslationKeyShort(item.interval as SubscriptionInterval) as string
                  }
                />
                <Badge
                  layout={isMobile ? 'green' : 'red-sale'}
                  size="xs"
                  className="ml-2 xl:inline-block"
                >
                  {discountPercentage && (
                    <FormattedMessage
                      id="pdp:buy-panel:order-type:subscription:cut-out-active"
                      values={{
                        value: `-${discountPercentage}%`,
                      }}
                    />
                  )}
                </Badge>
              </>
            ) : (
              <>
                <FormattedMessage id="cart:interval-selection:activate-subscription" />
                <Badge layout="red-sale" size="xs" className="ml-2 xl:inline-block">
                  {discountPercentage && (
                    <FormattedMessage
                      id="pdp:buy-panel:order-type:subscription:cut-out"
                      values={{
                        value: `-${discountPercentage}%`,
                      }}
                    />
                  )}
                </Badge>
              </>
            )}
          </button>
        )}
      </div>
      <ModalSlideUp
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        title={intl.formatMessage({ id: 'cart:subs-overlay:title' })}
      >
        <InlineIntervalSelector
          productId={item.productId}
          interval={interval}
          setInterval={setInterval}
          cancelInterval
        />
        <Button layout="brand" className="mt-4 w-full" onClick={() => handleClose()}>
          <FormattedMessage id="cart:subs-overlay:accept-selection" />
        </Button>
      </ModalSlideUp>
    </>
  );
};

export default SubscriptionButton;
